import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { AudioAsset, ListRowsResponse, Row } from '@tekvoice/shared';
import { useAuth } from '@/auth/auth-context';
import { AbDialog } from '@/components/review/ab-dialog';
import { BatchHeader } from '@/components/review/batch-header';
import { BulkBar } from '@/components/review/bulk-bar';
import { FilterChips } from '@/components/review/filter-chips';
import { ReviewProvider, type AbDialogPayload } from '@/components/review/review-context';
import { ReviewTable } from '@/components/review/review-table';
import { RowHistoryDrawer } from '@/components/review/row-history-drawer';
import { useGenerationFlow } from '@/components/review/use-generation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { countByStatus, rowMatchesStatus, type StatusFilter } from '@/lib/assets';

const ROWS_PAGE_SIZE = 1000;

/** SPEC §11 — the review table: the centre of the app. */
export function BatchDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [filter, setFilter] = React.useState<StatusFilter>('all');
  const [search, setSearch] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [historyRow, setHistoryRow] = React.useState<Row | null>(null);
  const [abPayload, setAbPayload] = React.useState<AbDialogPayload | null>(null);
  const [overrideRowId, setOverrideRowId] = React.useState<string | null>(null);

  const batchQuery = useQuery({
    queryKey: ['batch', id],
    queryFn: () => api.batches.get(id),
  });

  const rowsKey = React.useMemo(() => ['batch', id, 'rows'] as const, [id]);
  const rowsQuery = useQuery({
    queryKey: rowsKey,
    // Ordered by rowIndex server-side (SPEC §2); the client filters locally so the
    // chip counts stay live without a round trip per chip.
    queryFn: () => api.batches.rows(id, { pageSize: ROWS_PAGE_SIZE }),
  });

  const progressQuery = useQuery({
    queryKey: ['batch', id, 'progress'],
    queryFn: () => api.batches.progress(id),
    // SPEC §6 — poll every 2s while a run is RUNNING, then stop.
    refetchInterval: (query) => (query.state.data?.run?.status === 'RUNNING' ? 2_000 : false),
    enabled: Boolean(id),
  });

  /**
   * Everything about the run that the row payloads depend on. Watching the whole
   * signature — not just `run.status === 'RUNNING'` — matters: a small batch can
   * finish between starting the run and the first 2s poll, and keying off
   * "was running" alone would then leave the table showing stale statuses until
   * a manual reload.
   */
  const progressSignature = React.useMemo(() => {
    const data = progressQuery.data;
    if (!data) return null;
    return JSON.stringify([data.run?.id ?? null, data.run?.status ?? null, data.counts]);
  }, [progressQuery.data]);
  const runStatus = progressQuery.data?.run?.status ?? null;
  const previousSignature = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (progressSignature === null) return;
    const changed =
      previousSignature.current !== null && previousSignature.current !== progressSignature;
    previousSignature.current = progressSignature;
    // While a run is live the row payloads (statuses, takes, durations) change on
    // every tick; `changed` additionally covers the run finishing *before* the
    // first poll, which "was RUNNING" alone would miss.
    if (changed || runStatus === 'RUNNING') {
      // `exact` matters: `['batch', id]` is a PREFIX of the progress key, so a loose
      // invalidation here would refetch the very query this effect reacts to and
      // spin a refetch loop instead of the 2s poll SPEC §6 asks for.
      void queryClient.invalidateQueries({ queryKey: rowsKey, exact: true });
      void queryClient.invalidateQueries({ queryKey: ['batch', id], exact: true });
    }
  }, [progressSignature, runStatus, queryClient, rowsKey, id]);

  const applyRow = React.useCallback(
    (row: Row) => {
      queryClient.setQueryData<ListRowsResponse>(rowsKey, (previous) =>
        previous
          ? { ...previous, rows: previous.rows.map((candidate) => (candidate.id === row.id ? row : candidate)) }
          : previous,
      );
      // The row itself is already written into the cache above; only the batch
      // counters need a round trip (`exact` keeps it off rows/progress).
      void queryClient.invalidateQueries({ queryKey: ['batch', id], exact: true });
    },
    [queryClient, rowsKey, id],
  );

  const applyAsset = React.useCallback(
    (rowId: string, asset: AudioAsset) => {
      queryClient.setQueryData<ListRowsResponse>(rowsKey, (previous) =>
        previous
          ? {
              ...previous,
              rows: previous.rows.map((candidate) =>
                candidate.id === rowId
                  ? { ...candidate, assets: { ...candidate.assets, [asset.language]: asset } }
                  : candidate,
              ),
            }
          : previous,
      );
      void queryClient.invalidateQueries({ queryKey: ['batch', id], exact: true });
    },
    [queryClient, rowsKey, id],
  );

  /** Full refresh after a mutation. Every key is `exact` so nothing is refetched twice. */
  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: rowsKey, exact: true });
    void queryClient.invalidateQueries({ queryKey: ['batch', id], exact: true });
    void queryClient.invalidateQueries({ queryKey: ['batch', id, 'progress'], exact: true });
  }, [queryClient, rowsKey, id]);

  const generation = useGenerationFlow(id, invalidate);

  const allRows = React.useMemo(() => rowsQuery.data?.rows ?? [], [rowsQuery.data]);
  const counts = React.useMemo(() => countByStatus(allRows), [allRows]);

  const visibleRows = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allRows
      .filter((row) => (filter === 'all' ? true : rowMatchesStatus(row, filter)))
      .filter((row) =>
        needle.length === 0
          ? true
          : row.englishText.toLowerCase().includes(needle) || row.arabicText.includes(search.trim()),
      );
  }, [allRows, filter, search]);

  const selectedRows = React.useMemo(
    () => allRows.filter((row) => selectedIds.has(row.id)),
    [allRows, selectedIds],
  );

  const toggleRow = React.useCallback((rowId: string, selected: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (selected) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);

  const toggleAll = React.useCallback(
    (selected: boolean) => {
      setSelectedIds(selected ? new Set(visibleRows.map((row) => row.id)) : new Set());
    },
    [visibleRows],
  );

  const activeRun = progressQuery.data?.run ?? batchQuery.data?.batch.activeRun ?? null;
  const activeRunId = activeRun?.status === 'RUNNING' ? activeRun.id : null;
  // m2 — the review page keeps rendering for a soft-deleted batch (so an open tab does
  // not blow up), but every mutation is refused server-side, so hide the controls.
  const batchDeleted = Boolean(batchQuery.data?.batch.deletedAt);

  const reviewValue = React.useMemo(
    () => ({
      batchId: id,
      applyRow,
      applyAsset,
      invalidate,
      canEdit: can('row.edit') && !batchDeleted,
      canApprove: can('row.approve') && !batchDeleted,
      canUnapprove: can('row.unapprove') && !batchDeleted,
      canRegenerate: can('asset.regenerate') && !batchDeleted,
      activeRunId,
      batchDeleted,
      openHistory: setHistoryRow,
      openAb: setAbPayload,
      openOverride: setOverrideRowId,
      overrideRowId,
    }),
    [id, applyRow, applyAsset, invalidate, can, overrideRowId, activeRunId, batchDeleted],
  );

  if (batchQuery.isLoading || rowsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (batchQuery.isError || !batchQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Batch not found</CardTitle>
          <CardDescription>
            {batchQuery.error instanceof Error ? batchQuery.error.message : 'Unknown error'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
              Back to batches
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const batch = batchQuery.data.batch;

  return (
    <ReviewProvider value={reviewValue}>
      <div className="space-y-4">
        <BatchHeader
          batch={batch}
          progress={progressQuery.data}
          onRequestGenerate={(scope, label) => generation.request(scope, label)}
          onRunAborted={invalidate}
          canGenerate={can('asset.generate') && !batchDeleted}
          canExport={can('batch.export') && !batchDeleted}
          canAbort={can('run.abort') && !batchDeleted}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <FilterChips counts={counts} value={filter} onChange={setFilter} />
          <Input
            className="w-64"
            placeholder="Search text…"
            aria-label="Search rows"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <ReviewTable
          rows={visibleRows}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          emptyMessage={
            allRows.length === 0
              ? 'This batch has no rows.'
              : 'No rows match this filter — try another status chip or clear the search.'
          }
        />

        <BulkBar
          selectedRows={selectedRows}
          onClear={() => setSelectedIds(new Set())}
          onRequestGenerate={(scope, label) => generation.request(scope, label)}
          canGenerate={can('asset.generate') && !batchDeleted}
          canApprove={can('row.approve') && !batchDeleted}
        />

        {generation.dialog}
        <AbDialog payload={abPayload} onClose={() => setAbPayload(null)} />
        <RowHistoryDrawer row={historyRow} onClose={() => setHistoryRow(null)} />
      </div>
    </ReviewProvider>
  );
}
